import React, { useEffect, useState, useRef } from 'react';
import { ChevronDownIcon, X, Search } from 'lucide-react';
import { FilterChipProps } from './types';
import { DateRangePicker } from '../DateRangePicker';
export const FilterChip: React.FC<FilterChipProps> = ({
  filter,
  onSelect,
  onRemove,
  onCustomDateChange,
  'data-id': dataId
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [searchValue, setSearchValue] = useState(filter.selectedValue || '');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setShowDatePicker(false);
      }
    };
    if (isOpen || showDatePicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, showDatePicker]);
  // Focus input when opened for search filters
  useEffect(() => {
    if (isOpen && filter.type === 'search' && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, filter.type]);
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setIsOpen(!isOpen);
    } else if (event.key === 'Escape') {
      setIsOpen(false);
      setShowDatePicker(false);
    }
  };
  const handleSearchSubmit = () => {
    onSelect(searchValue);
    setIsOpen(false);
  };
  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleSearchSubmit();
    }
  };
  const handleOptionSelect = (value: string) => {
    if (value === 'custom') {
      setShowDatePicker(true);
      setIsOpen(false);
    } else {
      if (filter.multiSelect) {
        // For multi-select, toggle the value in the array
        const currentValues = filter.selectedValues || [];
        const newValues = currentValues.includes(value) ? currentValues.filter(v => v !== value) : [...currentValues, value];
        onSelect(newValues);
        // Don't close dropdown for multi-select to allow multiple selections
      } else {
        onSelect(value);
        setIsOpen(false);
        setShowDatePicker(false);
      }
    }
  };
  const handleDateRangeChange = (startDate: string, endDate: string) => {
    if (onCustomDateChange) {
      onCustomDateChange(filter.id, startDate, endDate);
    }
    onSelect('custom');
    setShowDatePicker(false);
  };
  const selectedOption = filter.options.find(opt => opt.value === filter.selectedValue);
  // Get selected options for multi-select filters
  const selectedOptions = filter.multiSelect ? filter.options.filter(opt => filter.selectedValues?.includes(opt.value)) : [];
  // Format display label for custom date range
  const getDisplayLabel = () => {
    // For search-type filters, show the search value
    if (filter.type === 'search') {
      if (filter.selectedValue && filter.selectedValue.trim()) {
        return `${filter.label}: "${filter.selectedValue}"`;
      }
      return filter.label;
    }
    // For multi-select filters, show the selected values
    if (filter.multiSelect && selectedOptions.length > 0) {
      if (selectedOptions.length === 1) {
        // Single selection - show "Label: Value"
        const option = selectedOptions[0];
        if (option.value === 'custom' && filter.customDateRange) {
          const start = new Date(filter.customDateRange.startDate).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
          });
          const end = new Date(filter.customDateRange.endDate).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
          });
          return `${filter.label}: ${start} - ${end}`;
        }
        return `${filter.label}: ${option.label}`;
      } else {
        // Multiple selections - show "Label: X selected"
        return `${filter.label}: ${selectedOptions.length} selected`;
      }
    }
    // For single-select filters
    if (filter.selectedValue === 'custom' && filter.customDateRange) {
      const start = new Date(filter.customDateRange.startDate).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
      const end = new Date(filter.customDateRange.endDate).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
      return `${filter.label}: ${start} - ${end}`;
    }
    return selectedOption ? `${filter.label}: ${selectedOption.label}` : filter.label;
  };
  const displayLabel = getDisplayLabel();
  return <div className="relative inline-block" ref={dropdownRef} data-id="element-3071" data-mp="3f1t208b0hh7f">
      <button onClick={() => setIsOpen(!isOpen)} onKeyDown={handleKeyDown} className={`
          h-10 pl-4 pr-16 flex items-center gap-2 text-[14px] font-medium
          bg-[#E0F2FE] border border-[#0EA5E9] rounded-md transition-all duration-200
          focus:outline-none focus:ring-2 focus:ring-[#0EA5E9]/20
          hover:bg-[#DBEAFE] text-[#0F172A]
        `} aria-haspopup="listbox" aria-expanded={isOpen} data-id="element-3072" data-mp="3jzuhykd550eo">
        <span className="whitespace-nowrap" data-id="element-3073" data-mp="2xrxgwvxwtc4y">{displayLabel}</span>
        {filter.type === 'search' ? <Search size={16} className="absolute right-8 text-[#475569] pointer-events-none" data-id="element-3074" data-mp="3vfpjk5fql5jx" /> : <ChevronDownIcon size={16} className={`absolute right-8 text-[#475569] transition-transform duration-200 pointer-events-none ${isOpen ? 'rotate-180' : ''}`} data-id="element-3075" data-mp="3qg32tm0p976d" />}
        {onRemove && <button onClick={e => {
        e.stopPropagation();
        onRemove();
      }} className="absolute right-2 p-0.5 hover:bg-[#0EA5E9]/20 rounded transition-colors" aria-label="Remove filter" data-id="element-3076" data-mp="3liultoraf96p">
            <X size={14} className="text-[#475569]" data-id="element-3077" data-mp="29zi5by8l9nr6" />
          </button>}
      </button>

      {/* Search input for search-type filters */}
      {isOpen && filter.type === 'search' && <div className="absolute top-full left-0 mt-2 min-w-[280px] bg-white border border-[#CBD5E1] rounded-lg shadow-lg z-[1001] p-4" data-id="element-3078" data-mp="27i6j3ablt40c">
          <h3 className="text-[14px] font-semibold text-[#0F172A] mb-3" data-id="element-3079" data-mp="1xjhc4cl8x3yv">
            Search {filter.label}
          </h3>
          <div className="space-y-3" data-id="element-3080" data-mp="22hiu0u6xqx8r">
            <input ref={inputRef} type="text" value={searchValue} onChange={e => setSearchValue(e.target.value)} onKeyDown={handleSearchKeyDown} placeholder={`Enter ${filter.label.toLowerCase()}...`} className="w-full h-10 px-3 text-[14px] border border-[#CBD5E1] rounded-md focus:outline-none focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20" data-id="element-3081" data-mp="22gh8tww7gudb" />
            <div className="flex gap-2" data-id="element-3082" data-mp="1jr16d40ymru9">
              <button type="button" onClick={() => {
            setSearchValue('');
            setIsOpen(false);
          }} className="flex-1 h-10 px-4 text-[14px] font-medium text-[#475569] bg-white border border-[#CBD5E1] rounded-md hover:bg-[#F1F5F9] transition-colors" data-id="element-3083" data-mp="1js3u532zke5y">
                Cancel
              </button>
              <button type="button" onClick={handleSearchSubmit} className="flex-1 h-10 px-4 text-[14px] font-medium text-white bg-[#0EA5E9] rounded-md hover:bg-[#0284C7] transition-colors" data-id="element-3084" data-mp="m69cs8zazrj7">
                Apply
              </button>
            </div>
          </div>
        </div>}

      {/* Dropdown for regular filters */}
      {isOpen && !showDatePicker && filter.type !== 'search' && <div className="absolute top-full left-0 mt-2 min-w-full bg-white border border-[#CBD5E1] rounded-md shadow-lg z-[1001] overflow-hidden" data-id="element-3085" data-mp="1a23wtqnslqpf">
          <ul role="listbox" className="py-1" data-id="element-3086" data-mp="1iws23gtdb8e8">
            {filter.options.map(option => {
          const isSelected = filter.multiSelect ? filter.selectedValues?.includes(option.value) : option.value === filter.selectedValue;
          return <li key={option.id} data-id="element-3087" data-mp="1sv7sfhkkmdmo">
                  <button onClick={() => handleOptionSelect(option.value)} className={`
                      w-full px-4 py-2.5 text-left text-[14px] transition-colors duration-200
                      hover:bg-[#F1F5F9] flex items-center gap-2
                      ${isSelected ? 'bg-[#E0F2FE] text-[#0EA5E9] font-medium' : 'text-[#475569]'}
                    `} role="option" aria-selected={isSelected} data-id="element-3088" data-mp="1nvmy5kgcitga">
                    {filter.multiSelect && <div className={`w-4 h-4 border-2 rounded flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-[#0EA5E9] border-[#0EA5E9]' : 'border-[#CBD5E1]'}`} data-id="element-3089" data-mp="396sft2u6komz">
                        {isSelected && <svg className="w-3 h-3 text-white" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor" data-id="element-3090" data-mp="2gk7qs916goml">
                            <path d="M5 13l4 4L19 7" data-id="element-3091" data-mp="2q9hivow0u7ap"></path>
                          </svg>}
                      </div>}
                    {option.label}
                  </button>
                </li>;
        })}
          </ul>
        </div>}

      {showDatePicker && <div className="absolute top-full left-0 mt-2 bg-white border border-[#CBD5E1] rounded-lg shadow-lg z-[1001] p-4 min-w-[280px]" data-id="element-3092" data-mp="1xqu3mq3phmhj">
          <h3 className="text-[14px] font-semibold text-[#0F172A] mb-3" data-id="element-3093" data-mp="1lac9coio35nj">
            Select Date Range
          </h3>
          <div className="space-y-3" data-id="element-3094" data-mp="1ga6p3teq8lfh">
            <div data-id="element-3095" data-mp="1g950sn8pa2ft">
              <label className="block text-[12px] font-medium text-[#475569] mb-1" data-id="element-3096" data-mp="1gcsubme5aouj">
                Start Date
              </label>
              <input type="date" value={filter.customDateRange?.startDate || ''} onChange={e => {
            if (onCustomDateChange && filter.customDateRange) {
              onCustomDateChange(filter.id, e.target.value, filter.customDateRange.endDate);
            }
          }} max={filter.customDateRange?.endDate || undefined} className="w-full h-10 px-3 text-[14px] border border-[#CBD5E1] rounded-md focus:outline-none focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20" data-id="element-3097" data-mp="17ategxev9qt6" />
            </div>
            <div data-id="element-3098" data-mp="jkgwsuttkfpe">
              <label className="block text-[12px] font-medium text-[#475569] mb-1" data-id="element-3099" data-mp="jm0aqes06eo0">
                End Date
              </label>
              <input type="date" value={filter.customDateRange?.endDate || ''} onChange={e => {
            if (onCustomDateChange && filter.customDateRange) {
              onCustomDateChange(filter.id, filter.customDateRange.startDate, e.target.value);
            }
          }} min={filter.customDateRange?.startDate || undefined} className="w-full h-10 px-3 text-[14px] border border-[#CBD5E1] rounded-md focus:outline-none focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20" data-id="element-3100" data-mp="10zfrxbdowoty" />
            </div>
            <div className="flex gap-2 pt-2" data-id="element-3101" data-mp="d5wlazk3r6hw">
              <button type="button" onClick={() => {
            setShowDatePicker(false);
            setIsOpen(false);
          }} className="flex-1 h-10 px-4 text-[14px] font-medium text-[#475569] bg-white border border-[#CBD5E1] rounded-md hover:bg-[#F1F5F9] transition-colors" data-id="element-3102" data-mp="r4e2eefi5kqu">
                Cancel
              </button>
              <button type="button" onClick={() => {
            if (filter.customDateRange?.startDate && filter.customDateRange?.endDate) {
              handleDateRangeChange(filter.customDateRange.startDate, filter.customDateRange.endDate);
            }
          }} disabled={!filter.customDateRange?.startDate || !filter.customDateRange?.endDate} className="flex-1 h-10 px-4 text-[14px] font-medium text-white bg-[#0EA5E9] rounded-md hover:bg-[#0284C7] disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-3103" data-mp="1ygmzcsb6zhnx">
                Apply
              </button>
            </div>
          </div>
        </div>}
    </div>;
};